import { useEffect, useRef } from 'react'
import { Cargando } from '../../componentes/Cargando'
import { EstadoError } from '../../componentes/EstadoError'
import { EstadoVacio } from '../../componentes/EstadoVacio'
import { IconoVolver } from '../../componentes/iconos'
import { Burbuja } from './Burbuja'
import type { Canal, SesionResumen } from './consultas'
import { useMensajes } from './consultas'
import { InsigniaAnimo } from './InsigniaAnimo'
import { PanelMetadatos } from './PanelMetadatos'

interface Props {
  canal: Canal
  /** El identificador tal cual esta en la base. Es la clave del sondeo. */
  sesionId: string
  /** El resumen del listado, si la sesion esta en la pagina cargada. */
  resumen: SesionResumen | null
  onVolver: () => void
}

export function Conversacion({ canal, sesionId, resumen, onVolver }: Props) {
  const consulta = useMensajes(canal, sesionId)
  const mensajes = consulta.data ?? []
  const fin = useRef<HTMLLIElement>(null)
  const ultimoId = mensajes.at(-1)?.id

  useEffect(() => {
    // Encadenado opcional: jsdom no implementa scrollIntoView y no hace falta
    // simularlo para nada de lo que se prueba.
    fin.current?.scrollIntoView?.({ block: 'end' })
  }, [ultimoId])

  // Sin resumen (la sesion no esta en la pagina cargada del listado) se usa el
  // identificador, que es lo unico que se sabe con certeza.
  const etiqueta = resumen?.etiqueta ?? sesionId
  const telefono = resumen?.telefono && resumen.telefono !== etiqueta ? resumen.telefono : null

  return (
    <section
      aria-label={`Conversación con ${etiqueta}`}
      // El panel es la superficie hundida (`lienzo`) y las burbujas del cliente
      // la elevada (`superficie`): esa es la jerarquia que las separa, y se
      // mantiene en los dos temas.
      className="flex min-w-0 flex-col gap-3 rounded-xl border border-borde bg-lienzo p-3"
    >
      <header className="flex flex-col gap-2">
        <div className="flex items-start gap-2">
          {/* Solo en movil: en escritorio el listado esta al lado y no hay de
              donde volver. */}
          <button
            type="button"
            onClick={onVolver}
            aria-label="Volver al listado de conversaciones"
            className="-ml-1 flex min-h-10 items-center gap-1 rounded-lg px-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado lg:hidden"
          >
            <IconoVolver aria-hidden="true" className="size-5" />
            <span>Volver</span>
          </button>

          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-tinta">{etiqueta}</h2>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-tinta-apagada">
              {telefono ? <span>{telefono}</span> : null}
              <span>{mensajes.length} mensajes</span>
              <InsigniaAnimo meta={resumen?.meta} />
            </p>
          </div>
        </div>

        <PanelMetadatos meta={resumen?.meta} />
      </header>

      {consulta.isPending ? (
        <Cargando mensaje="Cargando la conversación" />
      ) : consulta.isError ? (
        <EstadoError error={consulta.error} onReintentar={() => void consulta.refetch()} />
      ) : mensajes.length === 0 ? (
        <EstadoVacio
          titulo="Esta conversación no tiene mensajes"
          descripcion="El listado la cuenta, pero no ha llegado ninguna línea legible."
        />
      ) : (
        // La altura tambien esta limitada en movil, no solo a partir de `lg`.
        // Sin limite el que se desplaza es la pagina entera, y como al abrir la
        // conversacion se baja al ultimo mensaje, el boton de volver se queda
        // arriba del todo: medido con una conversacion de 254 mensajes a 375
        // px, a 31.000 px por encima de la pantalla. `dvh` y no `vh` porque en
        // el movil la barra del navegador aparece y desaparece.
        <ol className="flex max-h-[60dvh] flex-col gap-2 overflow-y-auto lg:max-h-[70vh]">
          {mensajes.map((mensaje) => (
            <Burbuja key={mensaje.id} mensaje={mensaje} />
          ))}
          {/* Ancla del scroll. `li` y no `div`: dentro de un `ol` solo caben
              elementos de lista. */}
          <li ref={fin} aria-hidden="true" />
        </ol>
      )}
    </section>
  )
}
